import util from 'preact-util';

/*
 * Pure client-side helpers for face-recognition consent. No module-level
 * mutable state.
 *
 * The age check here is for MESSAGING ONLY — the server gate in
 * backend/lib/face-consent.js is authoritative. This exists so the UI can
 * explain a refusal without a pointless round trip.
 *
 * See docs/superpowers/specs/2026-08-17-face-recognition-design.md
 */

export const MAX_ENROLL_IMAGES = 3;
export const MIN_ENROLL_AGE = 18;

export function emptyFaceProfile() {
    return {
        enrolled: false,
        allowFindMyPhotos: false,
        allowTagSuggestions: false,
        enrolledImageCount: 0,
    };
}

const asBool = value => value === true || value === 1 || value === '1' || value === 'true';

// Allowlist on the way in as well as out: even if a future server change leaked
// a Rekognition handle, it would not reach component state from here.
export function parseFaceProfile(value) {
    if (!value || typeof value !== 'object') {
        return emptyFaceProfile();
    }
    return {
        enrolled: asBool(value.enrolled),
        allowFindMyPhotos: asBool(value.allowFindMyPhotos),
        allowTagSuggestions: asBool(value.allowTagSuggestions),
        enrolledImageCount: Number(value.enrolledImageCount) || 0,
    };
}

/*
 * Kill switch. Face recognition needs a Rekognition collection and the
 * rekognition:* IAM grants applied per deployment; without them every enrolment
 * fails with a generic 400. The main build serves Musher, Raskepoter,
 * Soknadsguiden, LiveRace and others from the same bundle, so the panel is OFF
 * unless a tenant explicitly turns it on — same shape as
 * featureFlags.feedReturnFoundation / featureFlags.feedPersonalization.
 */
export function faceRecognitionEnabled(currentCustomer) {
    return parseInt(currentCustomer?.featureFlags?.faceRecognition, 10) === 1;
}

/*
 * Maps a server `message` onto a stable UI key. Without this every refusal —
 * no face in the photo, a group photo, already enrolled, under 18 — looked
 * identical to the user: nothing happened and the photo selection got cleared.
 * Unknown messages collapse to 'generic' rather than being shown raw, so a
 * server-side string is never rendered untranslated.
 */
export function faceErrorKey(response) {
    const known = {
        NO_FACE: 'noFace',
        MULTIPLE_FACES: 'multipleFaces',
        alreadyEnrolled: 'alreadyEnrolled',
        underage: 'underage',
        missingBirth: 'missingBirth',
        noImages: 'noImages',
        tooManyImages: 'tooManyImages',
        unknownImageKey: 'unknownImageKey',
        noConsentGiven: 'noConsentGiven',
        switchesRequired: 'switchesRequired',
        faceRecognitionDisabled: 'faceRecognitionDisabled',
    };
    return known[response && response.message] || 'generic';
}

export function enrolmentBlockedReason(user, now = new Date()) {
    const raw = user && user.birth;
    if (!raw) {
        return 'missingBirth';
    }
    const born = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(born.getTime())) {
        return 'missingBirth';
    }
    let age = now.getUTCFullYear() - born.getUTCFullYear();
    const monthDiff = now.getUTCMonth() - born.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < born.getUTCDate())) {
        age -= 1;
    }
    return age < MIN_ENROLL_AGE ? 'underage' : null;
}

export async function loadFaceProfile({ fetchApi = util.fetchApi } = {}) {
    try {
        const response = await fetchApi('/api/faces/profile', { method: 'GET' }, {});
        if (response?.status !== 200) {
            return null;
        }
        return parseFaceProfile(response.data);
    } catch (error) {
        return null;
    }
}

export default {
    MAX_ENROLL_IMAGES,
    MIN_ENROLL_AGE,
    emptyFaceProfile,
    parseFaceProfile,
    faceRecognitionEnabled,
    faceErrorKey,
    enrolmentBlockedReason,
    loadFaceProfile,
};
